import { SAVED_SEARCH_URL_PARAM } from '@aibees/elephant/context/search-params-url-sync';
import { useSearchTableScope } from '@aibees/elephant/context/search-table-scope';
import { useSearchHistoryStore } from '@aibees/elephant/store/search-history-store';
import {
  useSearchParamsStore,
  type SearchTableScope,
} from '@aibees/elephant/store/search-params-store';
import { SearchType } from '@aibees/elephant/types';
import type { SearchBrowseParams } from '@aibees/elephant/types';
import { useReadPresetApi } from '@aibees/shared/api';
import type { ParsedPreset } from '@aibees/shared/definition';
import { useNotify } from '@aibees/shared/hooks';
import {
  jsonParse,
  objSanitizeNilValues,
  parseUrlQueryString,
} from '@aibees/shared/utils';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

/**
 * Keys that can appear in a saved-search `value` (or its legacy `metadata.url`)
 * but are NOT applied filters:
 * - `type` — legacy saves merged the scope `SearchType` ('Company' | 'People').
 * - `name` / `visibility` — the preset's OWN fields. A preset update that only
 *   meant to change those can overwrite `value` with `{ name, visibility }` (the
 *   "Edit Saved Name" / change-visibility dialog did this — now fixed in
 *   `search-list-edit-action`, but presets corrupted before that fix still carry
 *   this shape). They must never be hydrated as filters, and don't count as
 *   "real filters" when deciding whether a source is usable.
 * - `page` — pagination; safe to apply, but not a filter on its own.
 */
const NON_FILTER_VALUE_KEYS = new Set(['type', 'name', 'visibility', 'page']);

/** Strip preset-meta / scope keys so only applied filters reach the store. */
function stripNonFilterKeys(
  value?: Partial<SearchBrowseParams>
): Partial<SearchBrowseParams> {
  if (!value) return {};
  const next = { ...value } as Record<string, unknown>;
  if (next.type === SearchType.Company || next.type === SearchType.People) {
    delete next.type;
  }
  // Never hydrate the preset's own fields as filters (guards the `{ name,
  // visibility }` clobber shape).
  delete next.name;
  delete next.visibility;
  return next as Partial<SearchBrowseParams>;
}

/** Does this params object carry at least one actual search filter? */
function hasFilterParams(value: Partial<SearchBrowseParams>): boolean {
  return Object.keys(value).some((key) => !NON_FILTER_VALUE_KEYS.has(key));
}

/**
 * Resolve the filter params to restore for a saved-search preset.
 *
 * Prefer the preset `value` (the canonical params object). But some presets have
 * a `value` that carries NO filters — most commonly `{ name, visibility }` left
 * behind when a rename / visibility change overwrote `value`. For those, fall
 * back to the legacy `metadata.url` query string, which still holds the original
 * filters. Returns null when neither source yields a filter — so the caller can
 * avoid wiping the current scope with an empty set.
 */
function resolveSavedSearchParams(
  preset?: ParsedPreset<Partial<SearchBrowseParams>>
): Partial<SearchBrowseParams> | null {
  if (!preset) return null;

  const fromValue = stripNonFilterKeys(preset.value);
  if (hasFilterParams(fromValue)) return fromValue;

  const metadata = jsonParse<{ url?: string }>(preset.metadata, {});
  if (metadata?.url) {
    const fromUrl = stripNonFilterKeys(
      parseUrlQueryString<Partial<SearchBrowseParams>>(metadata.url)
    );
    if (hasFilterParams(fromUrl)) return fromUrl;
  }

  return null;
}

/**
 * Reads the one-shot `?saved-search=<presetId>` link param, fetches that saved
 * search preset, replaces the current scope's params with its filters, then
 * strips the param from the URL.
 *
 * This is the saved-search counterpart of `useApplySharedSearch`: "View Search"
 * links carry only the short preset id (never the full filter set), so a new tab
 * can restore the saved filters without ever putting a long filter query string
 * in the URL (which is what could hit the web server's URL length limit / 414).
 *
 * The id is LATCHED from the initial URL because `saved-search` is excluded from
 * the filter sync (`omitNonFilterParams`); the sync would otherwise strip it
 * before the async fetch resolves.
 */
export function useApplySavedSearch() {
  const scope = useSearchTableScope();
  const [, setSearchParams] = useSearchParams();
  const replaceParamsForScope = useSearchParamsStore.useReplaceParamsForScope();
  const resetHistoryTo = useSearchHistoryStore.useResetTo();
  const notify = useNotify();

  const [savedSearchId] = useState<string | null>(() =>
    new URLSearchParams(window.location.search).get(SAVED_SEARCH_URL_PARAM)
  );
  const appliedRef = useRef(false);

  const hasScope =
    scope === 'company' || scope === 'people' || scope === 'employees';
  const shouldFetch = !!savedSearchId && hasScope && !appliedRef.current;

  const { data, isError, isSuccess } = useReadPresetApi<
    Partial<SearchBrowseParams>
  >(savedSearchId ?? undefined, { enabled: shouldFetch });

  const stripSavedSearchId = useCallback(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete(SAVED_SEARCH_URL_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  useEffect(() => {
    if (!savedSearchId || !hasScope || appliedRef.current) return;

    if (isError) {
      appliedRef.current = true;
      notify.error('This saved search could not be loaded.');
      stripSavedSearchId();
      return;
    }

    if (isSuccess) {
      appliedRef.current = true;
      const applied = resolveSavedSearchParams(data);

      // Nothing restorable (e.g. `value` clobbered with no `metadata.url`
      // fallback). "View Search" opens in a NEW TAB, which inherits a COPY of the
      // opener tab's sessionStorage (browser behavior — the store persists there),
      // so the scope is NOT empty: it shows the opener's last search (the latest
      // preset that was open on /search/people). Clear it so a failed restore
      // lands on a clean empty search + error, not an unrelated filter set.
      if (!applied) {
        notify.error('This saved search could not be loaded.');
        replaceParamsForScope(scope as SearchTableScope, {});
        resetHistoryTo(scope as SearchTableScope, {});
        stripSavedSearchId();
        return;
      }

      replaceParamsForScope(scope as SearchTableScope, applied);
      // Seed the applied state as the undo/redo baseline so the header "undo"
      // returns to the saved search (and can't drop below it into empty filters).
      resetHistoryTo(
        scope as SearchTableScope,
        objSanitizeNilValues(applied) as Record<string, string>
      );
      stripSavedSearchId();
    }
  }, [
    savedSearchId,
    hasScope,
    scope,
    data,
    isError,
    isSuccess,
    replaceParamsForScope,
    resetHistoryTo,
    notify,
    stripSavedSearchId,
  ]);
}
